import { Badge, Button, Group, Modal, SegmentedControl, Stack, Text, Textarea } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconFile, IconX } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { downloadFile, http } from "../../api/http";
import { DataTable } from "../../components/DataTable";
import { fmtDateTime, PageHeader, StatusBadge } from "../../components/ui";
import { useT } from "../../i18n/script";

type Req = Record<string, unknown> & { id: number; status: string; bpg: string; holder_name: string; nosilac_u_registru: string | null;
  bpg_postoji: boolean; document_id: number | null };

/** Zahtjevi eksternih korisnika za pristup svom gazdinstvu — odobrava Agencija ili opštinski referent (u svojoj opštini). */
export default function AccessRequestsPage() {
  const { t } = useT();
  const qc = useQueryClient();
  const [status, setStatus] = useState("na_cekanju");
  const [dec, setDec] = useState<{ r: Req; ok: boolean } | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const q = useQuery({ queryKey: ["access-requests", status], queryFn: () => http.get<Req[]>("/api/v1/access-requests", { status }) });

  async function decide() {
    if (!dec) return;
    setBusy(true);
    try {
      await http.post(`/api/v1/access-requests/${dec.r.id}/${dec.ok ? "approve" : "reject"}`, { note: note || null });
      notifications.show({ color: dec.ok ? "green" : "orange", message: t(dec.ok ? "Zahtjev je odobren — korisniku je dodijeljen BPG i rola proizvođača." : "Zahtjev je odbijen.") });
      setDec(null); setNote("");
      qc.invalidateQueries({ queryKey: ["access-requests"] });
    } catch (e) { notifications.show({ color: "red", message: String(e) }); } finally { setBusy(false); }
  }

  return (
    <Stack>
      <PageHeader title="Zahtjevi za pristup" description="Eksterni korisnici (nosioci gazdinstava) traže pristup podacima svog gazdinstva. Provjerite da li se nosilac iz zahtjeva poklapa sa Registrom i priloženi dokaz, pa odobrite ili odbijte uz obrazloženje. Odluka se evidentira u istoriji aktivnosti."
        right={<SegmentedControl value={status} onChange={setStatus} data={[{ value: "na_cekanju", label: t("Na čekanju") }, { value: "odobren", label: t("Odobreni") },
          { value: "odbijen", label: t("Odbijeni") }, { value: "sve", label: t("Svi") }]} />} />
      <DataTable rows={q.data ?? []} exportName="zahtjevi_pristup" columns={[
        { key: "created_at", label: "Podnesen", render: (r) => fmtDateTime(r.created_at) }, { key: "username", label: "Korisnik" },
        { key: "full_name", label: "Ime" }, { key: "email", label: "E-pošta" }, { key: "bpg", label: "BPG" },
        { key: "holder_name", label: "Nosilac (zahtjev)" },
        { key: "nosilac_u_registru", label: "Nosilac (registar)", render: (r) => r.bpg_postoji
          ? <Text size="sm" c={String(r.nosilac_u_registru ?? "").toLowerCase() === String(r.holder_name).toLowerCase() ? "green" : "orange"}>{String(r.nosilac_u_registru ?? "—")}</Text>
          : <Badge color="red" size="sm">{t("BPG ne postoji")}</Badge> },
        { key: "relation", label: "Odnos" }, { key: "opstina", label: "Opština" },
        { key: "document_id", label: "Dokaz", render: (r) => r.document_id ? <Button size="compact-xs" variant="subtle" leftSection={<IconFile size={12} />}
          onClick={() => downloadFile(`/api/v1/dms/documents/${r.document_id}/versions/1/file`, {}, true)}>{t("Otvori")}</Button> : "—" },
        { key: "status", label: "Status", render: (r) => <StatusBadge status={String(r.status)} /> },
        { key: "a", label: "", render: (r) => r.status === "na_cekanju" ? (
          <Group gap={4} wrap="nowrap">
            <Button size="compact-xs" color="green" leftSection={<IconCheck size={12} />} disabled={!r.bpg_postoji} onClick={() => setDec({ r: r as Req, ok: true })}>{t("Odobri")}</Button>
            <Button size="compact-xs" color="red" variant="light" leftSection={<IconX size={12} />} onClick={() => setDec({ r: r as Req, ok: false })}>{t("Odbij")}</Button>
          </Group>) : <Text size="xs" c="dimmed">{String(r.decided_by ?? "")} {String(r.decision_note ?? "")}</Text> },
      ]} />
      <Modal opened={!!dec} onClose={() => setDec(null)} title={dec ? t(dec.ok ? `Odobri pristup BPG ${dec.r.bpg}` : `Odbij zahtjev za BPG ${dec.r.bpg}`) : ""}>
        <Stack>
          {dec?.ok && <Text size="sm">{t("Korisniku se dodjeljuje atribut BPG i rola „proizvođač“ — vidjeće samo podatke ovog gazdinstva.")}</Text>}
          <Textarea label={t(dec?.ok ? "Napomena" : "Obrazloženje (obavezno)")} value={note} onChange={(e) => setNote(e.currentTarget.value)} autosize minRows={3} />
          <Group justify="flex-end">
            <Button color={dec?.ok ? "green" : "red"} loading={busy} disabled={!dec?.ok && !note} onClick={decide}>{t(dec?.ok ? "Odobri" : "Odbij")}</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
